import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeContext = createContext(null);

const THEMES = ["pastel", "neon", "swiss"];
const FONT_SCALES = { small: 0.9, normal: 1, large: 1.15, xlarge: 1.35 };

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => localStorage.getItem("bd_theme") || "pastel");
  const [fontScale, setFontScaleState] = useState(
    () => localStorage.getItem("bd_font") || "normal"
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("bd_theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.setProperty("--text-scale", FONT_SCALES[fontScale]);
    localStorage.setItem("bd_font", fontScale);
  }, [fontScale]);

  const cycleTheme = useCallback(() => {
    setThemeState((t) => THEMES[(THEMES.indexOf(t) + 1) % THEMES.length]);
  }, []);

  const setTheme = useCallback((t) => {
    if (THEMES.includes(t)) setThemeState(t);
  }, []);

  const setFontScale = useCallback((s) => {
    if (FONT_SCALES[s] !== undefined) setFontScaleState(s);
  }, []);

  // Memoize the context value so consumers don't re-render on every
  // parent render. The setters are already stable via useCallback above.
  const value = useMemo(
    () => ({ theme, fontScale, setTheme, setFontScale, cycleTheme, themes: THEMES }),
    [theme, fontScale, setTheme, setFontScale, cycleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
